/**
 * "Before you decide" (DESIGN §4.1): one sunken row under the control. It opens a Drawer with the decision's own sheet (what it does, the trade-off, how a banker decides),
 * so the control above never moves. Closed on every card; opening it marks nothing reviewed.
 */
import { useId, useState } from "react";
import type { ContentCatalog, DecisionCatalogEntry, MarketView, TeamState } from "@bsim/contracts";
import type { RiskProjection } from "../../api/riskFields";
import { useCoach } from "../../coach/CoachDrawer";
import { Drawer } from "../../theme/Drawer";
import { useBand } from "../../theme/breakpoints";
import { GxDescribed } from "../../theme/GxTerm";
import { DecisionSheetBody } from "../DecisionSheetBody";
import "./walk.css";

export function BeforeYouDecide({ entry, team, catalog, market, projection }: { readonly entry: DecisionCatalogEntry; readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView; readonly projection?: RiskProjection | undefined }) {
  const [open, setOpen] = useState(false);
  // On a phone it is a closed disclosure like its sibling "What this decision rests on" (MOBILE-ARCHITECTURE §4.2): one model for the two rows under the control. Wider, it is the drawer.
  const phone = useBand() === "phone";
  const panel = useId();
  const coach = useCoach();
  const card = catalog.conceptCards.find((c) => c.id === entry.coachCardIds[0]);
  const body = (
          <div id={panel} className="gx-walk-before-b" data-testid="walk.before.body">
            <DecisionSheetBody entry={entry} team={team} catalog={catalog} market={market} projection={projection} panel={false} />
            <div className="gx-sheet-f">
              {card === undefined ? null : <GxDescribed label={card.title} plain={card.summary} whyItMatters={card.whyItMatters} conceptCardId={card.id}>Read the idea behind it</GxDescribed>}
              <button type="button" className="gx-linkrow" data-testid="walk.before.coach" onClick={() => { setOpen(false); coach.open({ screen: "decide", label: entry.label, decisionId: entry.decisionId }); }}>Ask the Coach about this</button>
            </div>
          </div>
  );
  return (
    <div className="gx-walk-before" data-testid="walk.before" data-open={open}>
      <button type="button" className="gx-walk-before-t" aria-haspopup={phone ? undefined : "dialog"} aria-expanded={open} aria-controls={open ? panel : undefined} data-testid="walk.before.toggle" onClick={() => setOpen(phone ? !open : true)}>
        <b>Before you decide</b><span>What it does, the trade-off, how a banker decides <span aria-hidden="true">{phone && open ? "\u25BE" : "\u25B8"}</span></span>
      </button>
      {open && phone ? body : open ? (
        <Drawer title={entry.label} context={<span className="gx-small gx-faint">Before you decide</span>} onClose={() => setOpen(false)}>
          {body}
        </Drawer>
      ) : null}
    </div>
  );
}
